import { inject, injectable } from 'inversify';

import StorageService from '../../StorageService';
import { PromiseQueue } from '../../../utils/promiseQueue';
import { logDebug } from '../../../logger';
import env from '../../../env';

import type { RefreshTokenResponse } from './types';

const VD_OTT_TOKEN_KEY = 'auth';

type RequestOptions = {
  withAuthentication?: boolean;
  headers?: Record<string, string>;
};

type TokenObject = { token: string; refreshToken: string; expires: number };

// refresh the token when it expires within the value below
const TOKEN_REFRESH_TIME = 3600 * 24 * 3;

@injectable()
export default class VDApiService {
  protected readonly queue = new PromiseQueue<TokenObject>();
  protected isRefreshing = false;
  protected isSandbox = false;

  /**
   * The `logoutCallback` is a delegate that can be set to handle what should happen when the token is expired or not
   * valid anymore. Since this service shouldn't care about the UI or Zustand state, a controller can use this delegate
   * to update the UI and clear the logged in state. Alternatively, this could dispatch an event, but a delegate is
   * easier to align with the InPlayer integration.
   */
  private logoutCallback?: () => Promise<void>;

  constructor(@inject(StorageService) protected readonly storageService: StorageService) {}

  protected async refreshTokenIfNeeded(tokens: TokenObject): Promise<TokenObject | undefined> {
    if (this.isRefreshing) {
      return await this.queue.enqueue();
    }

    const tokenValidity = tokens.expires - Date.now() / 1000;

    if (tokenValidity < TOKEN_REFRESH_TIME) {
      logDebug('VDOTTApiService', 'refreshing tokens');

      this.isRefreshing = true;

      try {
        const response = await this.post<RefreshTokenResponse>(
          '/customers/refresh-token',
          {},
          {
            withAuthentication: false,
            headers: {
              Authorization: `Bearer ${tokens.token}`,
            },
          },
        );
        const refreshedTokenObject: TokenObject = {
          token: response.refreshedToken,
          expires: response.exp,
          refreshToken: '',
        };

        await this.setToken(refreshedTokenObject.token, '', refreshedTokenObject.expires);
        await this.queue.resolve(refreshedTokenObject);
        return refreshedTokenObject;
      } catch (error) {
        logDebug('VDOTTApiService', 'failed to refresh tokens', { error });
        await this.logoutCallback?.();
        await this.queue.resolve(); // don't reject
      } finally {
        this.isRefreshing = false;
      }
    }

    // the current tokens are still valid
    return tokens;
  }

  initialize = (sandbox: boolean, logoutCallback: () => Promise<void>) => {
    this.isSandbox = sandbox;
    this.logoutCallback = logoutCallback;
  };

  setToken = (token: string, refreshToken = '', expires: number) => {
    return this.storageService.setItem(VD_OTT_TOKEN_KEY, JSON.stringify({ token, refreshToken, expires }), true);
  };

  getToken = async () => {
    const tokenObject = await this.storageService.getItem(VD_OTT_TOKEN_KEY, true, true);

    if (tokenObject) {
      const authData = tokenObject as TokenObject;
      const refreshedTokens = await this.refreshTokenIfNeeded(authData);

      if (refreshedTokens) return refreshedTokens;
    }

    return { token: '', refreshToken: '', expires: 0 };
  };

  removeToken = async () => {
    await this.storageService.removeItem(VD_OTT_TOKEN_KEY, true);
  };

  isAuthenticated = async () => {
    const tokenObject = await this.getToken();

    return !!tokenObject.token && tokenObject.expires > Date.now() / 1000;
  };

  protected getBaseUrl() {
    return env.APP_API_BASE_URL;
  }

  private performRequest = async (
    path: string = '/',
    method = 'GET',
    body?: Record<string, unknown>,
    { withAuthentication = false, headers = {} }: RequestOptions = {},
    searchParams?: Record<string, string | number>,
  ) => {
    const requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...headers,
    };

    if (withAuthentication) {
      const tokenObject = await this.getToken();

      if (tokenObject.token) {
        requestHeaders.Authorization = `Bearer ${tokenObject.token}`;
      }
    }

    const searchParamsString = searchParams ? `?${new URLSearchParams(searchParams as Record<string, string>).toString()}` : '';
    const endpoint = `${this.getBaseUrl()}${path}${searchParamsString}`;

    const response = await fetch(endpoint, {
      headers: requestHeaders,
      method,
      body: body ? JSON.stringify(body) : undefined,
      credentials: 'include',
    });

    const parsedResponse = await response.json();

    if (!response.ok) {
      throw { response: { data: parsedResponse } };
    }

    return parsedResponse;
  };

  get = <T>(path: string, options?: RequestOptions, searchParams?: Record<string, string | number>) =>
    this.performRequest(path, 'GET', undefined, options, searchParams) as Promise<T>;

  patch = <T>(path: string, body?: Record<string, unknown>, options?: RequestOptions) => this.performRequest(path, 'PATCH', body, options) as Promise<T>;

  put = <T>(path: string, body?: Record<string, unknown>, options?: RequestOptions) => this.performRequest(path, 'PUT', body, options) as Promise<T>;

  post = <T>(path: string, body?: Record<string, unknown>, options?: RequestOptions) => this.performRequest(path, 'POST', body, options) as Promise<T>;

  remove = <T>(path: string, options?: RequestOptions, body?: Record<string, unknown>) => this.performRequest(path, 'DELETE', body, options) as Promise<T>;
}
